import React, { createContext, useContext, useState, useEffect } from "react";

const SchemaContext = createContext(null);

const STORAGE_KEY = "api-service-manager-schema";

export const SchemaProvider = ({ children }) => {
  const [schema, setSchema] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setSchema(parsed);
        setEndpoints(parsed.endpoints || []);
      }
    } catch (e) {
      console.error("Failed to load saved schema:", e);
    }
    setIsLoaded(true);
  }, []);

  // Save to localStorage when schema changes
  useEffect(() => {
    if (schema && isLoaded) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(schema));
      } catch (e) {
        console.error("Failed to save schema:", e);
      }
    }
  }, [schema, isLoaded]);

  const importSchema = (parsedSchema) => {
    setSchema(parsedSchema);
    setEndpoints(parsedSchema.endpoints || []);
  };

  const clearSchema = () => {
    setSchema(null);
    setEndpoints([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const updateEndpoint = (endpointId, updates) => {
    setEndpoints((prev) =>
      prev.map((ep) => (ep.id === endpointId ? { ...ep, ...updates } : ep))
    );
  };

  const deleteEndpoint = (endpointId) => {
    setEndpoints((prev) => prev.filter((ep) => ep.id !== endpointId));
  };

  const addMockResponse = (endpointId, mockData) => {
    setEndpoints((prev) =>
      prev.map((ep) => {
        if (ep.id === endpointId) {
          const mocks = ep.mocks || [];
          return { ...ep, mocks: [...mocks, mockData] };
        }
        return ep;
      })
    );
  };

  const value = {
    schema,
    endpoints,
    isLoaded,
    hasSchema: !!schema,
    importSchema,
    clearSchema,
    updateEndpoint,
    deleteEndpoint,
    addMockResponse,
    schemaInfo: schema?.info || null,
    schemaType: schema?.type || null,
  };

  return (
    <SchemaContext.Provider value={value}>{children}</SchemaContext.Provider>
  );
};

export const useSchema = () => {
  const context = useContext(SchemaContext);
  if (!context) {
    throw new Error("useSchema must be used within a SchemaProvider");
  }
  return context;
};

export default SchemaContext;
